
import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { CampSaveButton } from "../LocationFormSections/components/CampSaveButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

interface LocationFormHeaderProps {
  locationId?: string;
  versionId?: string;
  versionNumber?: number;
  isSaving: boolean;
  savedStatus: "unsaved" | "saving" | "saved";
  isDirty: boolean;
  changeDescription: string;
  onChangeDescriptionChange: (value: string) => void;
  lastSavedTime: Date | null;
  onManualSave?: () => void;
  isWelcomeScreen?: boolean;
}

const LocationFormHeader = ({
  locationId,
  versionId,
  versionNumber = 1,
  isSaving,
  savedStatus,
  isDirty,
  changeDescription,
  onChangeDescriptionChange,
  lastSavedTime,
  onManualSave,
  isWelcomeScreen = false
}: LocationFormHeaderProps) => {
  const [preserveVersion, setPreserveVersion] = useState(false);
  console.log("LocationFormHeader props:", {
    locationId,
    versionId,
    isSaving,
    savedStatus,
    isDirty,
    versionNumber,
    preserveVersion,
    changeDescription,
    isWelcomeScreen
  });

  // Effect to automatically set preserveVersion to true if a change description is entered
  useEffect(() => {
    if (changeDescription && changeDescription.trim().length > 0 && !preserveVersion) {
      setPreserveVersion(true);
    }
  }, [changeDescription]);

  // Effect to clear change description when preserveVersion is toggled off
  useEffect(() => {
    if (!preserveVersion && changeDescription) {
      onChangeDescriptionChange('');
    }
  }, [preserveVersion, changeDescription, onChangeDescriptionChange]);

  const handleSaveClick = () => {
    console.log("Save button clicked in LocationFormHeader", {
      preserveVersion,
      changeDescription,
      isWelcomeScreen
    });
    if (onManualSave) {
      console.log("Calling onManualSave directly from LocationFormHeader");
      onManualSave();
    } else {
      console.error("onManualSave callback is not defined!");
    }
  };

  const formatTimestamp = (timestamp: Date | null) => {
    if (!timestamp) return "";
    return format(timestamp, "h:mm:ss a");
  };

  return (
    <div className="space-y-0 p-2 bg-gray-50 rounded-lg sticky top-0 z-10">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <div
              className={`w-3 h-3 rounded-full ${
                savedStatus === "saving"
                  ? "bg-yellow-400"
                  : savedStatus === "saved"
                  ? "bg-green-400"
                  : "bg-red-400"
              }`}
            />
            <span className="text-sm">
              {savedStatus === "saving"
                ? "Saving..."
                : savedStatus === "saved"
                ? "All changes saved"
                : "Unsaved changes"}
            </span>
          </div>
          {lastSavedTime && (
            <p className="text-xs text-gray-400">
              Last saved at {formatTimestamp(lastSavedTime)}
            </p>
          )}
        </div>
        <div className="flex gap-2 items-center">
          {/* Only show the checkbox if not on welcome screen */}
          {!isWelcomeScreen && (
            <div className="flex items-center gap-2 mr-2">
              <Checkbox
                id="preserveVersion"
                checked={preserveVersion}
                onCheckedChange={(checked) => setPreserveVersion(checked === true)}
              />
              <Label htmlFor="preserveVersion" className="text-sm">
                Name this version
              </Label>
            </div>
          )}
          
          {preserveVersion && !isWelcomeScreen && (
            <div className="min-w-[200px]">
              <Label htmlFor="changeDescription" className="sr-only">
                Version name
              </Label>
              <Input
                id="changeDescription"
                placeholder="Enter version name..."
                value={changeDescription}
                onChange={(e) => onChangeDescriptionChange(e.target.value)}
                className="text-sm"
              />
            </div>
          )}
          
          <CampSaveButton
            saving={isSaving}
            type="button"
            onClick={handleSaveClick}
            isDirty={isDirty}
            isWelcomeScreen={isWelcomeScreen}
          />
        </div>
      </div>
    </div>
  );
};

export default LocationFormHeader;
